import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { WORK } from "@/data/site";
import { SectionHead, Reveal } from "@/components/common/Reveal";
import { WorkMockup } from "@/components/home/WorkMockups";

const Visual = ({ type }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <div ref={ref} className="relative overflow-hidden rounded-xl bg-[#030712] p-6 sm:p-10 [clip-path:inset(0_round_12px)]">
      <div className="grid-dark pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-600/25 blur-[90px]" />
      <motion.div style={{ y }} className="relative"><WorkMockup type={type} /></motion.div>
    </div>
  );
};

const Entry = ({ w, i }) => {
  const isDemo = !w.client;
  return (
    <article className="grid items-center gap-10 border-t border-slate-200 py-14 lg:grid-cols-12 lg:gap-16" data-testid={`work-entry-${w.id}`}>
      <Reveal className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-blue-600/30 bg-blue-50 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-blue-800" data-testid={`work-label-${w.id}`}>
            {isDemo ? "Capability Demonstration" : "Case Study"}
          </span>
          <span className="font-mono text-[12px] text-slate-500">{w.category}</span>
        </div>
        <h3 className="mt-6 text-2xl font-extrabold tracking-[-0.02em] text-slate-950 sm:text-3xl">{w.title}</h3>
        {w.client && <p className="mt-2 text-[14px] font-semibold text-slate-700">{w.client}</p>}
        <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{w.text}</p>
        <ul className="mt-6 flex flex-wrap gap-2">
          {w.tags.map((t) => <li key={t} className="rounded border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700">{t}</li>)}
        </ul>
        {w.url && <a href={w.url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-semibold text-slate-950 underline-offset-4 hover:underline">View project <ExternalLink className="h-4 w-4" /></a>}
        {isDemo && <p className="mt-6 text-[13px] leading-relaxed text-slate-500">Illustrative concept showing the kind of system we design. Not a client project.</p>}
      </Reveal>
      <Reveal delay={0.1} className={`lg:col-span-7 ${i % 2 ? "lg:order-1" : ""}`}><Visual type={w.ui} /></Reveal>
    </article>
  );
};

export const Work = ({ index = "05" }) => (
  <section id="work" className="bg-white py-24 lg:py-32" data-testid="work-section">
    <div className="container-q">
      <SectionHead index={index} label="Featured work" title="Capability demonstrations."
        text="Client case studies will be published here once projects are cleared for disclosure. Until then, these reference concepts show the kinds of systems we build." />
      <div className="mt-16">{WORK.map((w, i) => <Entry key={w.id} w={w} i={i} />)}</div>
    </div>
  </section>
);
